import React from 'react';

import { InlineAlert } from '@rbilabs/universal-components';
import { Pressable, StyleSheet, View } from 'react-native';

import { primitive, spacing } from 'styles/constants';

import { OfferContent, OfferHeader, OfferImage } from './components';
import { StackedCardContainer } from './components/stacked-card-container';
import { useOfferListItem } from './hooks/use-offer-list-item';
import type { OfferListItemProps } from './types';

export const OfferListItem = ({
  description,
  serviceModeStyle,
  serviceModeText,
  handleRemove,
  header,
  id,
  image,
  imageAlt,
  imageUri,
  offerApplied,
  offerText,
  onPress,
  promoCodeLabel,
  removeOfferText,
  unavailable,
  unavailableMessage,
  unavailableIcon,
  isWalmartOffer,
  isWalmartUser,
  isWalmartPaidMember,
  endDate,
  expirationDate,
  engineId,
  ...buttonProps
}: OfferListItemProps) => {
  const {
    isHovered,
    setIsHovered,
    offersCooldown,
    expiresAt,
    configOfferDateRange,
    isVisible,
    showWalmartBadge,
    offerIsApplied,
    walmartBadgeImage,
  } = useOfferListItem({
    id,
    offerApplied,
    offerText,
    handleRemove,
    removeOfferText,
    isWalmartOffer,
    isWalmartUser,
    endDate,
    expirationDate,
  });

  if (!isVisible) {
    return null;
  }

  const {
    style,
    isGroup,
    isPartnershipOffer,
    activateButtonLabel,
    onPartnershipActivate,
    linkedButtonLabel,
    ...restButtonProps
  } = buttonProps as any;

  const pressedAvailableColor = '#F5F5F5';
  const pressedUnavailableColor = '#E0D9CC';

  const isUnavailable = (unavailable && unavailableMessage) || offersCooldown.isActive;

  const content = (
    <View
      style={[
        !isGroup && styles.contentContainer,
        !isGroup &&
          (offerIsApplied ? styles.contentContainerApplied : styles.contentContainerDefault),
        !isGroup && (unavailable ? styles.borderBottomZero : styles.borderBottomRadius),
      ]}
    >
      <OfferHeader
        offerApplied={offerApplied}
        offerText={offerText}
        handleRemove={handleRemove}
        removeOfferText={removeOfferText}
      />
      <View style={styles.mainContent}>
        <OfferImage image={image} imageUri={imageUri} imageAlt={imageAlt} />
        <OfferContent
          header={header}
          description={description}
          isHovered={isHovered}
          expiresAt={expiresAt}
          configOfferDateRange={configOfferDateRange}
          showWalmartBadge={!!showWalmartBadge}
          walmartBadgeImage={walmartBadgeImage}
          promoCodeLabel={promoCodeLabel}
          serviceModeText={serviceModeText}
          serviceModeStyle={serviceModeStyle}
          isGroup={isGroup}
          isPartnershipOffer={
            buttonProps.isPartnershipOffer ?? (buttonProps as any).isPartnershipOffer
          }
          activateButtonLabel={buttonProps.activateButtonLabel}
          onPartnershipActivate={buttonProps.onPartnershipActivate}
          linkedButtonLabel={buttonProps.linkedButtonLabel}
          isWalmartUser={isWalmartUser}
        />
      </View>
    </View>
  );

  return (
    <Pressable
      accessibilityRole="button"
      dd-action-name={header}
      onPress={!offersCooldown.isActive ? onPress : undefined}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      dataSet={{
        sanityId: id,
        engineId,
        availability: unavailable ? 'unavailable' : 'available',
      }}
      style={({ pressed }) => {
        const pressedColor = isUnavailable ? pressedUnavailableColor : pressedAvailableColor;

        const baseStyles = [
          styles.container,
          isGroup
            ? style
            : [
                styles.offerListItemBox,
                isUnavailable && styles.offerListItemBoxUnavailable,
                offerApplied && styles.offerListItemBoxApplied,
                style,
                pressed && { backgroundColor: pressedColor },
              ],
        ];

        if (isGroup && pressed && onPress) {
          baseStyles.push({ opacity: 0.7 });
        }

        return baseStyles;
      }}
      {...restButtonProps}
      testID="offer-list-item"
    >
      {isGroup ? (
        <StackedCardContainer
          isActive={offersCooldown.isActive}
          isFocused={false}
          padding={
            buttonProps.isPartnershipOffer
              ? [`${spacing.$0}`, `${spacing.$0}`]
              : [`${spacing.$3}`, `${spacing.$4}`]
          }
        >
          {content}
        </StackedCardContainer>
      ) : (
        <>
          {content}
          {unavailable && !offerApplied && (
            <InlineAlert
              borderBottomRadius={10}
              borderTopRadius="0"
              width="full"
              status={unavailableIcon || 'info'}
              message={unavailableMessage}
              w="full"
            />
          )}
        </>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 10,
    width: '100%',
  },
  offerListItemBox: {
    backgroundColor: primitive.$white,
    borderRadius: 10,
    borderWidth: 0,
    borderColor: 'transparent',
  },
  offerListItemBoxUnavailable: {
    backgroundColor: primitive.$disabledNoOpacity,
  },
  offerListItemBoxApplied: {
    borderWidth: 2,
    borderColor: primitive.$bbqBrown,
  },
  contentContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    backgroundColor: 'transparent',
  },
  contentContainerApplied: {
    paddingTop: 4,
  },
  contentContainerDefault: {
    paddingTop: 12,
  },
  borderBottomRadius: {
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
  borderBottomZero: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  mainContent: {
    flexDirection: 'row',
    marginRight: 8,
  },
});
